import { Component, Element, Event, EventEmitter, forceUpdate, h, Host, Prop, State, Watch } from "@stencil/core";
import { UxErrorEvent } from "../../globals/event-types";
import { LoaderVariant } from "../loader/loader.enum";
import { i18nProvider } from "@trumpf/ux.i18n";

@Component({
    tag: "ux-image",
    styleUrl: "image.scss",
    shadow: true
})
export class Image {
    @Element() el!: HTMLUxImageElement;

    /**
     * The source URL of the image.
     * @required
     */
    @Prop() readonly src!: string;
    /**
     * The alternative text for the image.
     * @required
     */
    @Prop() readonly alt!: string;
    /**
     * The loading strategy for the image.
     * Can be either "lazy" or "eager".
     * @default "eager"
     */
    @Prop() readonly loading: "lazy" | "eager" = "eager";
    /**
     * The width of the image in pixels.
     * @optional
     */
    @Prop() readonly width?: number;
    /**
     * The height of the image in pixels.
     * @optional
     */
    @Prop() readonly height?: number;
    /**
     * The source set attribute for responsive images.
     * @optional
     */
    @Prop() readonly srcset?: string;
    /**
     * The sizes attribute for responsive images.
     * @optional
     */
    @Prop() readonly sizes?: string;
    /**
     * defines how the image fit its container
     */
    @Prop() readonly objectFit?: "fill" | "contain" | "cover" | "scale-down" | "none" = "fill";
    /**
     * The cross-origin attribute for the image.
     * Can be "anonymous", "use-credentials", or undefined.
     * @optional
     */
    @Prop() readonly crossorigin: "anonymous" | "use-credentials" | undefined;
    /**
     * Whether to show a spinner while the image is loading.
     * @default false
     */
    @Prop() readonly spinner: boolean = false;
    /**
     * Whether to show an error state without emitting the error event.
     * @default false
     */
    @Prop() readonly error: boolean = false;

    /**
     * The list of source elements for the picture element.
     * @default []
     */
    @State() sourceElements: HTMLSourceElement[] = [];

    private observer!: MutationObserver;

    /**
     * Emitted when the image is loaded and displayed.
     * @event uxReady
     */
    @Event({
        eventName: "uxReady"
    })
    readyEvent!: EventEmitter<void>;

    /**
     * Emitted when the image cannot be displayed by the component.
     * @event uxError
     */
    @Event({
        eventName: "uxError"
    })
    errorEvent!: EventEmitter<UxErrorEvent>;

    private i18nSubscriberId!: string;

    connectedCallback() {
        this.i18nSubscriberId = i18nProvider.subscribe(() => forceUpdate(this));
    }

    componentWillLoad() {
        this.#updateSourceElements();
        this.setDimensions();
    }

    componentDidLoad() {
        this.observer = new MutationObserver(() => {
            this.#updateSourceElements();
        });
        this.observer.observe(this.el, { childList: true, subtree: true, attributes: true });
    }

    @Watch("height")
    @Watch("width")
    setDimensions() {
        if (this.height === undefined) {
            this.el.style.setProperty("--ux-image-height", "auto");
            this.el.style.setProperty("--ux-image-inner-height", "auto");
        } else {
            this.el.style.setProperty("--ux-image-height", `${this.height}px`);
            this.el.style.setProperty("--ux-image-inner-height", "100%");
        }
        this.el.style.setProperty("--ux-image-placeholder-padding-top", `${((this.height || 200) / (this.width || 300)) * 100}%`);
        this.el.style.setProperty("--ux-image-inner-position", this.height && this.width ? "absolute" : "relative");
    }

    disconnectedCallback() {
        this.observer.disconnect();
        i18nProvider.unsubscribe(this.i18nSubscriberId);
    }

    #updateSourceElements() {
        const sourceElements = Array.from(this.el.querySelectorAll("source"));
        this.sourceElements = sourceElements;
    }

    render() {
        const { error, spinner, src, alt, loading, width, height, srcset, sizes, crossorigin, sourceElements, objectFit } = this;
        if (error) {
            const { t } = i18nProvider;
            const errorMessage = t("contentLoadingError");
            return (
                <Host role="img" aria-label={errorMessage}>
                    <div class="placeholder-container">
                        <div class="placeholder-content">
                            <ux-icon class="error-icon" name="cross"></ux-icon>
                            <div class="error-message">{errorMessage}</div>
                            <div class="error-alt-text">{alt}</div>
                        </div>
                    </div>
                </Host>
            );
        }

        if (spinner) {
            return (
                <Host role="img" aria-label={alt} aria-busy="true">
                    <div class="placeholder-container">
                        <div class="placeholder-content">
                            <ux-loader variant={"spinner" as LoaderVariant}></ux-loader>
                        </div>
                    </div>
                </Host>
            );
        }

        return (
            <Host role="img" aria-label={alt}>
                {Boolean(this.height && this.width) && <div class="placeholder-container"></div>}
                <picture>
                    {sourceElements.map((sourceElement) => {
                        return (
                            <source
                                srcSet={sourceElement.srcset || undefined}
                                media={sourceElement.media || undefined}
                                type={sourceElement.type || undefined}
                                width={sourceElement.width || undefined}
                                height={sourceElement.height || undefined}
                            />
                        );
                    })}
                    <img
                        src={src}
                        alt={alt}
                        srcset={srcset}
                        sizes={sizes}
                        loading={loading}
                        width={width}
                        height={height}
                        crossorigin={crossorigin}
                        onError={(e: Event) => {
                            this.errorEvent.emit({ originalError: e });
                        }}
                        onLoad={() => {
                            this.readyEvent.emit();
                        }}
                        {...(objectFit !== "fill" && { style: { objectFit: objectFit } })}
                    />
                </picture>
            </Host>
        );
    }
}
